// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent, Analytics } from "firebase/analytics";
// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyB5aP7YHsjk89kCwVkto9rsqWf7WOUjktY",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "mocktrail-50ed2.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "mocktrail-50ed2",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "mocktrail-50ed2.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "549023735860",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:549023735860:web:efdbbd5141be1212ee6a61",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-72T398EMXZ"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// UTM Parameter interface
interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

// Function to extract UTM parameters from URL
export const extractUTMParams = (): UTMParams => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};
  
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      utmParams[param as keyof UTMParams] = value;
    }
  });
  
  return utmParams;
};

// Function to get stored UTM parameters
export const getStoredUTMParams = (): UTMParams => {
  try {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Function to store UTM parameters
export const storeUTMParams = (params: UTMParams): void => {
  try {
    if (Object.keys(params).length > 0) {
      sessionStorage.setItem('utm_params', JSON.stringify(params));
    }
  } catch (error) {
    console.error('Failed to store UTM params:', error);
  }
};

// Initialize UTM tracking
export const initializeUTMTracking = (): void => {
  const currentUTM = extractUTMParams();
  if (Object.keys(currentUTM).length > 0) {
    storeUTMParams(currentUTM);
  }
};

// Enhanced analytics helper functions with UTM tracking
export const trackPageView = (pageName: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Page view tracked: ${pageName}`, { utmParams, additionalParams });
  }
  
  // Also track in Clarity
  trackClarityEvent('page_view', {
    page_title: pageName,
    page_path: window.location.pathname,
    ...additionalParams
  });
};

export const trackSectionView = (sectionName: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'section_view', {
      section_name: sectionName,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Section view tracked: ${sectionName}`, { utmParams, additionalParams });
  }
};

export const trackButtonClick = (buttonName: string, buttonLocation: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, `button_click_${buttonName}_${buttonLocation}`, {
      button_name: buttonName,
      button_location: buttonLocation,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Button click tracked: ${buttonName} in ${buttonLocation}`, { utmParams, additionalParams });
  }
  
  // Also track in Clarity
  trackClarityEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
    page_path: window.location.pathname,
    ...additionalParams
  });
};

export const trackFormSubmission = (formName: string, success: boolean, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'form_submit', {
      form_name: formName,
      success: success,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Form submission tracked: ${formName}`, { success, utmParams, additionalParams });
  }
  
  // Also track in Clarity
  trackClarityEvent('form_submit', {
    form_name: formName,
    success: success,
    page_path: window.location.pathname,
    ...additionalParams
  });
};

export const trackUserInteraction = (interactionType: string, details: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'user_interaction', {
      interaction_type: interactionType,
      interaction_details: details,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`User interaction tracked: ${interactionType} - ${details}`, { utmParams, additionalParams });
  }
  
  // Also track in Clarity
  trackClarityEvent('user_interaction', {
    interaction_type: interactionType,
    interaction_details: details,
    page_path: window.location.pathname,
    ...additionalParams
  });
};

export const trackCustomEvent = (eventName: string, eventParams: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, eventName, {
      page_path: window.location.pathname,
      ...utmParams,
      ...eventParams
    });
    console.log(`Custom event tracked: ${eventName}`, { utmParams, eventParams });
  }
  
  // Also track in Clarity
  trackClarityEvent(eventName, {
    page_path: window.location.pathname,
    ...eventParams
  });
};

export const trackError = (errorType: string, errorMessage: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    logEvent(analytics, 'error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      page_path: window.location.pathname,
      ...utmParams,
      ...additionalParams
    });
    console.log(`Error tracked: ${errorType}`, { errorMessage, utmParams, additionalParams });
  }
};

export const trackPopupBlocked = (authMethod: string, location: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    const uniqueId = `mt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logEvent(analytics, 'popup_blocked', {
      auth_method: authMethod,
      location: location,
      unique_id: uniqueId,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
      browser: detectBrowser(),
      device_type: detectDeviceType(),
      ...utmParams,
      ...additionalParams
    });
    console.log(`Popup blocked tracked: ${authMethod} at ${location}`, { utmParams, additionalParams });
  }
};

export const trackAuthAttempt = (authMethod: string, location: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    const uniqueId = `mt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logEvent(analytics, 'auth_attempt', {
      auth_method: authMethod,
      location: location,
      unique_id: uniqueId,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
      browser: detectBrowser(),
      device_type: detectDeviceType(),
      ...utmParams,
      ...additionalParams
    });
    console.log(`Auth attempt tracked: ${authMethod} at ${location}`, { utmParams, additionalParams });
  }
};

export const trackAuthSuccess = (authMethod: string, location: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    const uniqueId = `mt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logEvent(analytics, 'auth_success', {
      auth_method: authMethod,
      location: location,
      unique_id: uniqueId,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
      browser: detectBrowser(),
      device_type: detectDeviceType(),
      ...utmParams,
      ...additionalParams
    });
    console.log(`Auth success tracked: ${authMethod} at ${location}`, { utmParams, additionalParams });
  }
};

export const trackAuthFailure = (authMethod: string, location: string, errorType: string, errorMessage: string, additionalParams?: Record<string, any>) => {
  if (analytics) {
    const utmParams = getStoredUTMParams();
    const uniqueId = `mt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    logEvent(analytics, 'auth_failure', {
      auth_method: authMethod,
      location: location,
      error_type: errorType,
      error_message: errorMessage,
      unique_id: uniqueId,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
      browser: detectBrowser(),
      device_type: detectDeviceType(),
      ...utmParams,
      ...additionalParams
    });
    console.log(`Auth failure tracked: ${authMethod} at ${location}`, { errorType, errorMessage, utmParams, additionalParams });
  }
};

// Helper functions for browser and device detection
const detectBrowser = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  return 'Unknown';
};

const detectDeviceType = (): string => {
  const userAgent = navigator.userAgent;
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Mac/i.test(userAgent)) return 'Mac';
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'Unknown';
};

// Microsoft Clarity Integration
export const initializeMicrosoftClarity = (): void => {
  const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  
  if (!clarityProjectId) {
    console.warn('Microsoft Clarity project ID not found in environment variables');
    return;
  }

  try {
    (function(c: any, l: any, a: any, r: any, i: any, t: any, y: any) {
      c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments) };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", clarityProjectId, null, null);
    
    console.log('Microsoft Clarity initialized successfully');
    
    // Set initial page context
    setTimeout(() => {
      setClarityTag('app_name', 'MockTrail');
      setClarityTag('app_version', '1.0');
      setClarityTag('initial_page', window.location.pathname);
    }, 1000);
  } catch (error) {
    console.error('Failed to initialize Microsoft Clarity:', error);
  }
};

// Clarity custom events
export const trackClarityEvent = (eventName: string, details?: Record<string, any>) => {
  try {
    if ((window as any).clarity) {
      (window as any).clarity('event', eventName, details);
      console.log(`Clarity event tracked: ${eventName}`, details);
    }
  } catch (error) {
    console.error('Failed to track Clarity event:', error);
  }
};

// Set Clarity custom tags
export const setClarityTag = (tagName: string, tagValue: string) => {
  try {
    if ((window as any).clarity) {
      (window as any).clarity('set', tagName, tagValue);
      console.log(`Clarity tag set: ${tagName} = ${tagValue}`);
    }
  } catch (error) {
    console.error('Failed to set Clarity tag:', error);
  }
};

export { analytics };